/**
 * ByodConfigPage.tsx — Bring Your Own Database configuration
 *
 * Allows org admins to:
 *   - View current BYOD status (hosted vs self-hosted)
 *   - Configure external database connection
 *   - Validate the connection
 *   - Monitor migration progress
 *   - View health check status
 *   - Remove BYOD config (revert to hosted)
 *
 * @module packages/billing-admin/src/pages/ByodConfigPage
 * @since 2026-06-20 (Platform Upgrade — Phase 4.8)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ByodStatus {
  configured: boolean;
  config?: {
    id: string;
    provider: string;
    schemaName: string;
    status: string;
    lastHealthCheck: string | null;
    lastHealthStatus: string | null;
    tablesMigrated: string[];
    createdAt: string;
  };
  hostingFeePct: string;
  isSelfHosted: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  pending_validation: { label: 'Pending Validation', color: 'bg-yellow-100 text-yellow-700' },
  validating: { label: 'Validating...', color: 'bg-blue-100 text-blue-700' },
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  migration_in_progress: { label: 'Migrating...', color: 'bg-purple-100 text-purple-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
};

const PROVIDERS = [
  { value: 'postgres', label: 'PostgreSQL', description: 'Standard PostgreSQL 14+' },
  { value: 'timescaledb', label: 'TimescaleDB', description: 'PostgreSQL with time-series extensions' },
  { value: 'clickhouse', label: 'ClickHouse', description: 'Column-oriented analytics database' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ByodConfigPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const orgId = activeOrg?.orgId ?? '';
  const queryClient = useQueryClient();

  const [showConfigForm, setShowConfigForm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [provider, setProvider] = useState('postgres');
  const [connectionString, setConnectionString] = useState('');
  const [schemaName, setSchemaName] = useState('billing_data');

  // ─── Data fetching ─────────────────────────────────────────────────────
  const { data: status, isLoading } = useQuery<ByodStatus>({
    queryKey: ['org', orgId, 'byod-status'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/byod/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    enabled: !!token && !!orgId,
    refetchInterval: 15000, // Auto-refresh for migration progress
  });

  // ─── Mutations ─────────────────────────────────────────────────────────
  const configureMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/byod/configure`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, connectionString, schemaName }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'byod-status'] });
      setShowConfigForm(false);
      setConnectionString('');
    },
  });

  const validateMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/byod/validate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Validation failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'byod-status'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/byod/configure`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Remove failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'byod-status'] });
      setShowRemoveConfirm(false);
    },
  });

  // ─── Render ────────────────────────────────────────────────────────────
  const config = status?.config;
  const isActive = config?.status === 'active';
  const isMigrating = config?.status === 'migration_in_progress';
  const statusBadge = config ? STATUS_BADGES[config.status] ?? { label: config.status, color: 'bg-slate-100 text-slate-600' } : null;

  return (
    <AdminLayout title="Database Configuration">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Bring Your Own Database</h2>
          <p className="mt-1 text-sm text-slate-500">
            Connect your own database to store billing telemetry data. This removes the 0.5% hosting fee.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
          </div>
        ) : (
          <>
            {/* ─── Current Status Card ──────────────────────────────────── */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Current Configuration</h3>
                  <div className="mt-2 flex items-center gap-3">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      status?.isSelfHosted ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {status?.isSelfHosted ? 'Self-Hosted (BYOD)' : 'Rule42 Hosted'}
                    </span>
                    {statusBadge && (
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadge.color}`}>
                        {statusBadge.label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Hosting Fee</p>
                  <p className="text-lg font-semibold text-slate-900">{status?.hostingFeePct ?? '0.5'}%</p>
                </div>
              </div>

              {config && (
                <div className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                  <div>
                    <p className="text-xs text-slate-500">Provider</p>
                    <p className="text-sm font-medium text-slate-800 capitalize">{config.provider}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Schema</p>
                    <p className="text-sm font-medium text-slate-800">{config.schemaName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Last Health Check</p>
                    <p className="text-sm text-slate-700">
                      {config.lastHealthCheck ? new Date(config.lastHealthCheck).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Health Status</p>
                    <p className={`text-sm font-medium ${config.lastHealthStatus === 'healthy' ? 'text-green-600' : 'text-amber-600'}`}>
                      {config.lastHealthStatus ?? 'Unknown'}
                    </p>
                  </div>
                  {config.tablesMigrated.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">Tables Migrated</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {config.tablesMigrated.map((t) => (
                          <span key={t} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex gap-2 border-t border-slate-100 pt-4">
                {!config && (
                  <button
                    onClick={() => setShowConfigForm(true)}
                    className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700"
                  >
                    Configure BYOD
                  </button>
                )}
                {config && !isActive && !isMigrating && (
                  <button
                    onClick={() => validateMutation.mutate()}
                    disabled={validateMutation.isPending}
                    className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                  >
                    {validateMutation.isPending ? 'Validating...' : 'Validate & Activate'}
                  </button>
                )}
                {config && (
                  <button
                    onClick={() => setShowRemoveConfirm(true)}
                    className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    Remove BYOD
                  </button>
                )}
              </div>

              {validateMutation.isError && (
                <p className="mt-2 text-xs text-red-600">{String(validateMutation.error)}</p>
              )}
              {validateMutation.isSuccess && (
                <p className="mt-2 text-xs text-green-600">Validation successful! Connection active.</p>
              )}
            </div>

            {/* ─── Migration Progress (if migrating) ──────────────────── */}
            {isMigrating && (
              <div className="rounded-xl border border-purple-200 bg-purple-50 p-6">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-purple-500 border-t-transparent" />
                  <div>
                    <h4 className="text-sm font-semibold text-purple-800">Data Migration in Progress</h4>
                    <p className="text-xs text-purple-600 mt-0.5">
                      Historical billing data is being transferred to your database. This may take several minutes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Benefits Card ──────────────────────────────────────── */}
            {!config && (
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Why BYOD?</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-green-600 text-[10px]">✓</span>
                    <span><strong className="text-slate-800">Eliminate hosting fee</strong> — Remove the 0.5% hosting charge entirely</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-green-600 text-[10px]">✓</span>
                    <span><strong className="text-slate-800">Data sovereignty</strong> — Keep all billing telemetry in your own infrastructure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-green-600 text-[10px]">✓</span>
                    <span><strong className="text-slate-800">Custom analytics</strong> — Query usage/audit data directly in your own tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-green-600 text-[10px]">✓</span>
                    <span><strong className="text-slate-800">Zero downtime</strong> — Dual-write migration ensures no data loss</span>
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── Configuration Form Modal ─────────────────────────────────── */}
      {showConfigForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">Configure External Database</h3>
            <p className="mt-1 text-sm text-slate-500">
              Provide your database connection details. The connection string is encrypted at rest.
            </p>

            <div className="mt-5 space-y-4">
              {/* Provider selection */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">Database Provider</label>
                <div className="grid grid-cols-3 gap-2">
                  {PROVIDERS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setProvider(p.value)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        provider === p.value
                          ? 'border-cyan-500 bg-cyan-50 ring-1 ring-cyan-500'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <p className="text-xs font-medium text-slate-800">{p.label}</p>
                      <p className="mt-0.5 text-[10px] text-slate-500">{p.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Connection string */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Connection String</label>
                <input
                  type="password"
                  value={connectionString}
                  onChange={(e) => setConnectionString(e.target.value)}
                  placeholder="postgresql://user:password@host:5432/dbname"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                />
                <p className="mt-1 text-[10px] text-slate-400">This will be AES-256-GCM encrypted before storage.</p>
              </div>

              {/* Schema name */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Schema Name</label>
                <input
                  value={schemaName}
                  onChange={(e) => setSchemaName(e.target.value)}
                  placeholder="billing_data"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                />
                <p className="mt-1 text-[10px] text-slate-400">Tables will be created under this schema in your database.</p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => { setShowConfigForm(false); setConnectionString(''); }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => configureMutation.mutate()}
                disabled={!connectionString || configureMutation.isPending}
                className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
              >
                {configureMutation.isPending ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>

            {configureMutation.isError && (
              <p className="mt-3 text-xs text-red-600">{String(configureMutation.error)}</p>
            )}
          </div>
        </div>
      )}

      {/* ─── Remove Confirmation ──────────────────────────────────────── */}
      <ConfirmDialog
        open={showRemoveConfirm}
        title="Remove BYOD Configuration"
        message="This will disconnect your external database and revert to Rule42-hosted storage. The 0.5% hosting fee will be re-applied. Your external database will NOT be deleted."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => removeMutation.mutate()}
        onCancel={() => setShowRemoveConfirm(false)}
        loading={removeMutation.isPending}
      />
    </AdminLayout>
  );
}
